import React, { useCallback, useMemo, useState } from "react";
import { format, formatISO, parse } from "date-fns";
import { timeFormat } from "@common/helpers/date";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import styles from "./SidePanel.module.scss";

interface Props {
    value: string;
    onChange: (value: string) => void;
}

interface TimeOption {
    value: string;
    label: string;
}

const TIME_OPTIONS: TimeOption[] = Array.from({ length: 48 }, (_, index) => {
    const totalMinutes = index * 30;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const paddedHours = String(hours).padStart(2, "0");
    const paddedMinutes = String(minutes).padStart(2, "0");
    return {
        value: `${paddedHours}:${paddedMinutes}`,
        label: format(new Date(2000, 0, 1, hours, minutes), timeFormat),
    };
});

function splitDateTime(value: string): { date: string; time: string } {
    if (!value) {
        return { date: "", time: "" };
    }
    const [date, time] = value.split("T");
    return { date: date ?? "", time: time ? time.slice(0, 5) : "" };
}

/** Build a fully-qualified ISO 8601 datetime (with timezone offset) from a
 *  `YYYY-MM-DD` date and an `HH:mm` time. */
function buildISODateTime(dateStr: string, timeStr: string): string {
    const dateTime = parse(
        `${dateStr} ${timeStr}`,
        "yyyy-MM-dd HH:mm",
        new Date(),
    );
    return formatISO(dateTime);
}

function timeLabel(time: string): string | undefined {
    return TIME_OPTIONS.find((option) => option.value === time)?.label;
}

export const ScheduleSendAtField: React.FC<Props> = ({ value, onChange }) => {
    const { date, time } = useMemo(() => splitDateTime(value), [value]);

    // When the picked date is today, slots at or before the current time are
    // not schedulable (the server rejects past sends), so gray them out. Both
    // sides are "HH:mm", which compares chronologically as plain strings.
    const [now, setNow] = useState(() => new Date());
    const handleTimeOpenChange = useCallback((isOpen: boolean) => {
        if (isOpen) {
            setNow(new Date());
        }
    }, []);
    const isToday = date === formatISO(now, { representation: "date" });
    const currentTime = format(now, "HH:mm");
    const timeItems: Array<PickerItemData<string>> = TIME_OPTIONS.map(
        (option) => ({
            ...option,
            disabled: isToday && option.value <= currentTime,
        }),
    );

    // Picking a date never presets a time: until the user explicitly chooses
    // one, the value stays date-only ("yyyy-MM-dd", no "T" part) and the time
    // select keeps its placeholder — scheduling stays blocked meanwhile.
    const handleDateChange = useCallback(
        (newDate: string | undefined) => {
            if (!newDate) {
                onChange("");
                return;
            }
            onChange(time ? buildISODateTime(newDate, time) : newDate);
        },
        [onChange, time],
    );

    const handleTimeChange = useCallback(
        (newTime: string | null) => {
            if (!newTime) {
                return;
            }
            const baseDate =
                date || formatISO(new Date(), { representation: "date" });
            onChange(buildISODateTime(baseDate, newTime));
        },
        [onChange, date],
    );

    return (
        <div className={styles.fieldGrid}>
            <DatePicker
                mode="day"
                variant="panel-input"
                className={styles.datePicker}
                clearable
                value={date || undefined}
                onValueChange={handleDateChange}
                placeholder="Select date"
                buttonProps={{ variant: "primary", size: "xl" }}
            />
            <Picker.Select
                items={timeItems}
                value={time || null}
                onValueChange={handleTimeChange}
                onOpenChange={handleTimeOpenChange}
                placeholder="Select time"
                renderValue={() => timeLabel(time)}
                trigger={<Picker.Button variant="primary" size="xl" />}
            />
        </div>
    );
};
